import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { IShadowTableData } from 'src/app/interfaces/IShadowTableData';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';
import { CandidateCount } from 'src/app/models/CandidateCount';
import { ElectionService } from 'src/app/services/election.service';

@Component({
  selector: 'two-cp-by-place-and-vote-type',
  templateUrl: './two-cp-by-place-and-vote-type.component.html',
  styleUrls: ['./two-cp-by-place-and-vote-type.component.scss']
})
export class TwoCpByPlaceAndVoteTypeComponent implements AfterViewInit, OnDestroy {
  @Input() title = 'Two Candidate Preferred by voting centre & vote type';

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public columns: ITableHeader[] = [];
  public candidateHeaders: { column: string; label: string; labelTwo: string; colourCode: string | null; colspan: number }[] = [];
  public candidateHeaderColumns: string[] = [];
  public shadowTable: IShadowTableData[][] = [];
  public dataSource: any = null;
  public displayedColumns: string[] = [];
  public hasData = false;
  public tableDistrictKey = '';

  constructor(
    public electionService: ElectionService,
    private cdr: ChangeDetectorRef
  ) {
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
      if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
        this.loadChart();
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
      this.loadChart();
    });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart();
    }
  }

  public customTrackBy(index: any, item: any) { return item.column; }

  trackByDistrictAndColumn(index: number, item: { column: string }): string {
    return (this.tableDistrictKey || '') + '_' + item.column;
  }

  private loadChart(): void {
    const page = this.electionService.selectedPage;
    if (page.pageType !== PageTypeEnum.CountHADistrict || !page.countDistrict) {
      this.tableDistrictKey = '';
      this.hasData = false;
      this.columns = [];
      this.candidateHeaders = [];
      this.candidateHeaderColumns = [];
      this.shadowTable = [];
      this.displayedColumns = [];
      this.dataSource = new MatTableDataSource([]);
      this.earlyVotingFirstRow = '';
      this.declarationFirstRow = '';
      this.absentFirstRow = '';
      this.cdr.markForCheck();
      return;
    }
    this.tableDistrictKey = page.countDistrict ?? '';
    this.hasData = false;
    this.columns = [];
    this.candidateHeaders = [];
    this.candidateHeaderColumns = [];
    this.shadowTable = [];
    this.displayedColumns = [];
    this.dataSource = new MatTableDataSource([]);
    this.earlyVotingFirstRow = '';
    this.declarationFirstRow = '';
    this.absentFirstRow = '';
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    const foundDistrict = election?.electionCount?.getDistrict(page.countDistrict);
    if (foundDistrict && foundDistrict.twoCPCandidate1 !== null && foundDistrict.twoCPCandidate2 !== null) {
      // Candidate header row (first row): place, candidate1 (colspan 2), candidate2 (colspan 2), total (colspan 2)
      this.candidateHeaders = [
        { column: 'headerPlace', label: '', labelTwo: '', colourCode: null, colspan: 1 },
        {
          column: 'headerC1',
          label: foundDistrict.twoCPCandidate1.candidateName,
          labelTwo: foundDistrict.twoCPCandidate1.partyId,
          colourCode: foundDistrict.twoCPCandidate1.colourCode,
          colspan: 2
        },
        {
          column: 'headerC2',
          label: foundDistrict.twoCPCandidate2.candidateName,
          labelTwo: foundDistrict.twoCPCandidate2.partyId,
          colourCode: foundDistrict.twoCPCandidate2.colourCode,
          colspan: 2
        },
        { column: 'headerTotal', label: 'Total Votes', labelTwo: '', colourCode: null, colspan: 2 }
      ];
      this.candidateHeaderColumns = this.candidateHeaders.map(h => h.column);

      // Column header row (second row): Polling place, Votes, %, Votes, %, Votes, %
      this.columns.push({
        column: '0',
        label: 'Polling place',
        labelTwo: '',
        isTwoRowLabel: false,
        minWidth: 120,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });

      this.columns.push({
        column: '1',
        label: 'Votes',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 96,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: '2',
        label: '%',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: true,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });

      this.columns.push({
        column: '3',
        label: 'Votes',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 96,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: '4',
        label: '%',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: true,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });

      this.columns.push({
        column: '5',
        label: 'Votes',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 96,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });
      this.columns.push({
        column: '6',
        label: '%',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: true,
        isColour: false,
        isColourBar: false,
        colourCode: null
      });

      // Define polling booth types (using short codes from API)
      const pollingBoothTypes = [
        'PB', 'Polling Booth',
        'MT', 'Mobile Polling Team',
        'S1', 'Shared Booth (Host)',
        'S2', 'Shared Booth (Visitor 1)',
        'S3', 'Shared Booth (Visitor 2)'
      ];
      const earlyVotingTypes = [
        'PP', 'Early Voting Centre',
        'P1', 'Shared Early Voting Centre (Parent)',
        'P2', 'Shared Early Voting Centre (Visitor 1)',
        'P3', 'Shared Early Voting Centre (Visitor 2)', 
        'P4', 'Shared Early Voting Centre (Host)',

      ];

      // Mobile Poll Team types sort last
      const isMobilePollTeam = (type: string): boolean =>
        type === 'MT' || type === 'Mobile Poll Team' || type === 'Mobile Polling Team';

      // Filter and sort polling places by type (Mobile Poll Team at last)
      const pollingBooths = foundDistrict.pollingPlaces
        .filter(p => pollingBoothTypes.includes(p.pollingPlaceType))
        .sort((a, b) => {
          const aMobile = isMobilePollTeam(a.pollingPlaceType);
          const bMobile = isMobilePollTeam(b.pollingPlaceType);
          if (aMobile !== bMobile) return aMobile ? 1 : -1;
          return a.pollingPlaceName < b.pollingPlaceName ? -1 : 1;
        });

      const earlyVotingCentres = foundDistrict.pollingPlaces
        .filter(p => earlyVotingTypes.includes(p.pollingPlaceType))
        .sort((a, b) => a.pollingPlaceName < b.pollingPlaceName ? -1 : 1);

      // Initialize totals for each section
      const pollingBoothTotals = [0, 0, 0, 0]; // [unused, candidate1, candidate2, total]
      const earlyVotingTotals = [0, 0, 0, 0];
      const declarationTotals = [0, 0, 0, 0];

      // Helper function to create a row (percentages derived from votes unless overrides provided)
      const createRow = (
        label: string,
        c1Votes: number,
        c2Votes: number,
        isBold: boolean,
        c1PercentOverride?: number | null,
        c2PercentOverride?: number | null
      ): IShadowTableData[] => {
        const total = c1Votes + c2Votes;
        const c1Percent =
          c1PercentOverride != null && c2PercentOverride != null
            ? c1PercentOverride / 100
            : total > 0
              ? c1Votes / total
              : 0;
        const c2Percent =
          c1PercentOverride != null && c2PercentOverride != null
            ? c2PercentOverride / 100
            : total > 0
              ? c2Votes / total
              : 0;
        const totalPercent = total > 0 ? 1 : 0; // 1 = 100% for percent pipe
        return [
          { isNumber: false, numberData: 0, stringData: label, isBold },
          { isNumber: true, numberData: c1Votes, stringData: '', isBold },
          { isNumber: true, numberData: c1Percent, stringData: '', isBold },
          { isNumber: true, numberData: c2Votes, stringData: '', isBold },
          { isNumber: true, numberData: c2Percent, stringData: '', isBold },
          { isNumber: true, numberData: total, stringData: '', isBold },
          { isNumber: true, numberData: totalPercent, stringData: '', isBold }
        ];
      };

      // Build absent ordinary groups (used for Absent - Polling Day / Early Voting rows)
      const normalizeAbsentType = (type: string): string =>
        (type || '')
          .replace(/\s*-\s*Absent\s+Declaration\s*\d*\s*$/i, '')
          .replace(/\s*-\s*Declaration\s*\d*\s*$/i, '')
          .trim() || type || 'Absent ordinary';

      /**
       * Absent ordinary 2CP counts for Result Entry / results display: use **only**
       * `twoCandidatePrefVotes` (do not fall back to `votes` / first preferences).
       */
      const normTokens = (s: string): string =>
        (s || '')
          .replace(/,/g, ' ')
          .split(/\s+/)
          .filter(Boolean)
          .map((x) => x.trim().toLowerCase())
          .sort()
          .join(' ');

      const normalizeCandidateLabel = (raw: string): string => {
        let s = (raw || '').trim();
        if (/^.+,\s*.+$/.test(s)) {
          s = s.replace(/^([^,]+),\s*(.+)$/, '$2 $1').trim();
        }
        return normTokens(s);
      };

      const findAbsentOrdinaryVoteRow = (
        candidateVotes: {
          candidateId?: number | string;
          candidateFullName?: string;
          twoCandidatePrefVotes?: number | null;
        }[],
        c: CandidateCount
      ) => {
        const byStaticId = candidateVotes.find(
          (v) => Number(v.candidateId) === Number(c.candidateId)
        );
        if (byStaticId) return byStaticId;
        const byBallotId = candidateVotes.find(
          (v) => Number(v.candidateId) === Number(c.changeCandidateId)
        );
        if (byBallotId) return byBallotId;
        const normC = normalizeCandidateLabel(c.candidateName || '');
        return (
          candidateVotes.find(
            (v) => normalizeCandidateLabel(String(v.candidateFullName || '')) === normC
          ) ??
          candidateVotes.find(
            (v) => normTokens(String(v.candidateFullName || '')) === normTokens(c.candidateName || '')
          )
        );
      };

      const getAbsentOrdinaryTwoCpVotesForCandidate = (
        candidateVotes:
          | {
              candidateId?: number | string;
              candidateFullName?: string;
              twoCandidatePrefVotes?: number | null;
            }[]
          | undefined,
        c: CandidateCount
      ): number => {
        if (!candidateVotes?.length) return 0;
        const row = findAbsentOrdinaryVoteRow(candidateVotes, c);
        if (row == null) return 0;
        if (row.twoCandidatePrefVotes != null) {
          return Number(row.twoCandidatePrefVotes);
        }
        return 0;
      };

      const absentList = foundDistrict.absentOrdinary ?? [];
      const absentGroups = new Map<string, { formal: number; c1: number; c2: number }>();
      const c1Ref = foundDistrict.twoCPCandidate1;
      const c2Ref = foundDistrict.twoCPCandidate2;
      absentList.forEach((abs) => {
        const category = normalizeAbsentType(abs.absentOrdinaryType);
        const formal = abs.formalVotes ?? 0;
        const c1 = c1Ref ? getAbsentOrdinaryTwoCpVotesForCandidate(abs.candidateVotes, c1Ref) : 0;
        const c2 = c2Ref ? getAbsentOrdinaryTwoCpVotesForCandidate(abs.candidateVotes, c2Ref) : 0;
        const existing = absentGroups.get(category);
        if (existing) {
          existing.formal += formal;
          existing.c1 += c1;
          existing.c2 += c2;
        } else {
          absentGroups.set(category, { formal, c1, c2 });
        }
      });
      const getAbsentByCategory = (name: string): { c1: number; c2: number } => {
        const key = Array.from(absentGroups.keys()).find((k) => k.toLowerCase() === name.toLowerCase());
        if (!key) return { c1: 0, c2: 0 };
        const g = absentGroups.get(key)!;
        return { c1: g.c1, c2: g.c2 };
      };
      const pollDayAbsent = getAbsentByCategory('Polling Day');
      const earlyVotingAbsent = getAbsentByCategory('Early Voting');

      // Process polling booth rows
      pollingBooths.forEach(pollingPlace => {
        const foundPP1 = pollingPlace.pollingCandidates.find(x => x.candidateName === foundDistrict.twoCPCandidate1?.candidateName);
        const foundPP2 = pollingPlace.pollingCandidates.find(x => x.candidateName === foundDistrict.twoCPCandidate2?.candidateName);
        const c1Votes = foundPP1?.twoCandidatePref ?? 0;
        const c2Votes = foundPP2?.twoCandidatePref ?? 0;

        pollingBoothTotals[1] += c1Votes;
        pollingBoothTotals[2] += c2Votes;
        pollingBoothTotals[3] += c1Votes + c2Votes;

        this.shadowTable.push(createRow(pollingPlace.pollingPlaceName, c1Votes, c2Votes, false));
      });

      // Polling day absent ordinary votes (non-bold), then total including it
      this.shadowTable.push(createRow('Polling day absent ordinary votes', pollDayAbsent.c1, pollDayAbsent.c2, false));
      this.shadowTable.push(createRow('Total polling booth ordinary votes', pollingBoothTotals[1] + pollDayAbsent.c1, pollingBoothTotals[2] + pollDayAbsent.c2, true));

      // Process early voting centre rows
      if (earlyVotingCentres.length > 0) {
        this.earlyVotingFirstRow = earlyVotingCentres[0].pollingPlaceName;
        earlyVotingCentres.forEach(pollingPlace => {
          const foundPP1 = pollingPlace.pollingCandidates.find(x => x.candidateName === foundDistrict.twoCPCandidate1?.candidateName);
          const foundPP2 = pollingPlace.pollingCandidates.find(x => x.candidateName === foundDistrict.twoCPCandidate2?.candidateName);
          const c1Votes = foundPP1?.twoCandidatePref ?? 0;
          const c2Votes = foundPP2?.twoCandidatePref ?? 0;

          earlyVotingTotals[1] += c1Votes;
          earlyVotingTotals[2] += c2Votes;
          earlyVotingTotals[3] += c1Votes + c2Votes;

          this.shadowTable.push(createRow(pollingPlace.pollingPlaceName, c1Votes, c2Votes, false));
        });
      }

      // Early voting absent ordinary votes (non-bold), then total including it
      this.shadowTable.push(createRow('Early voting absent ordinary votes', earlyVotingAbsent.c1, earlyVotingAbsent.c2, false));
      this.shadowTable.push(createRow('Total early voting ordinary votes', earlyVotingTotals[1] + earlyVotingAbsent.c1, earlyVotingTotals[2] + earlyVotingAbsent.c2, true));

      // Declaration votes - one row per declaration type (grouped), 2CP distributed by formal vote share
      const declC1Total = foundDistrict.twoCPCandidate1?.twoCPDeclarationVotes ?? 0;
      const declC2Total = foundDistrict.twoCPCandidate2?.twoCPDeclarationVotes ?? 0;
      declarationTotals[1] = declC1Total;
      declarationTotals[2] = declC2Total;
      declarationTotals[3] = declC1Total + declC2Total;

      const normalizeDeclarationType = (type: string): string =>
        (type || '').replace(/\s*-\s*Declaration\s*\d*\s*$/i, '').trim() || type || 'Declaration';

      const declarations = foundDistrict.declarations ?? [];
      const declarationGroups = new Map<
        string,
        {
          formal: number;
          c1Detail: number;
          c2Detail: number;
          /** True if any declaration in this group has candidateVotes with twoCandidatePrefVotes set. */
          hasTwoCpFromApi: boolean;
        }
      >();
      declarations.forEach((decl) => {
        const category = normalizeDeclarationType(decl.declarationType);
        const existing = declarationGroups.get(category);
        const formal = decl.formalVotes ?? 0;
        const hasRow2Cp =
          decl.candidateVotes?.some(
            (v) => v.twoCandidatePrefVotes !== undefined && v.twoCandidatePrefVotes !== null
          ) ?? false;
        const c1d = c1Ref ? getAbsentOrdinaryTwoCpVotesForCandidate(decl.candidateVotes, c1Ref) : 0;
        const c2d = c2Ref ? getAbsentOrdinaryTwoCpVotesForCandidate(decl.candidateVotes, c2Ref) : 0;
        if (existing) {
          existing.formal += formal;
          existing.c1Detail += c1d;
          existing.c2Detail += c2d;
          existing.hasTwoCpFromApi = existing.hasTwoCpFromApi || hasRow2Cp;
        } else {
          declarationGroups.set(category, {
            formal,
            c1Detail: c1d,
            c2Detail: c2d,
            hasTwoCpFromApi: hasRow2Cp
          });
        }
      });
      const declarationFormalTotal = foundDistrict.districtFormalDeclarationVotes ?? 0;
      const declarationDisplayOrder = [
        'Polling Day',
        'Early Voting',
        'Postal',
        'Electoral Visitor',
        'Mobile',
        'Telephone & Other',
        'Telephone',
        'Interstate',
        'Overseas'
      ];
      const getDeclarationSortIndex = (category: string): number => {
        const c = category.toLowerCase();
        const idx = declarationDisplayOrder.findIndex(
          (k) => c.includes(k.toLowerCase()) || k.toLowerCase().includes(c)
        );
        return idx >= 0 ? idx : 999;
      };
      const getDeclarationDisplayLabel = (category: string): string => {
        const c = category.toLowerCase();
        if (c.includes('polling day')) return 'Polling day declaration votes';
        if (c.includes('early voting')) return 'Early voting declaration votes';
        if (c.includes('postal')) return 'Postal votes';
        if (c.includes('electoral visitor') || c.includes('mobile')) return 'Electoral Visitor/ Mobile polling declaration votes';
        if (c.includes('telephone') || c.includes('interstate') || c.includes('overseas') || c.includes('other')) return 'Telephone/ Interstate/ Overseas declaration votes';
        return category.toLowerCase().includes('declaration') ? category : category + ' declaration';
      };
      const declarationCategoryOrder = Array.from(declarationGroups.keys()).sort((a, b) => {
        const idxA = getDeclarationSortIndex(a);
        const idxB = getDeclarationSortIndex(b);
        if (idxA !== idxB) return idxA - idxB;
        return a < b ? -1 : 1;
      });
      if (declarationCategoryOrder.length > 0) {
        this.declarationFirstRow = getDeclarationDisplayLabel(declarationCategoryOrder[0]);
      } else if (declC1Total > 0 || declC2Total > 0) {
        this.declarationFirstRow = 'Declaration Votes';
      }
      declarationCategoryOrder.forEach((category) => {
        const group = declarationGroups.get(category)!;
        const share = declarationFormalTotal > 0 ? group.formal / declarationFormalTotal : 0;
        const c1 = group.hasTwoCpFromApi
          ? group.c1Detail
          : Math.round(declC1Total * share);
        const c2 = group.hasTwoCpFromApi
          ? group.c2Detail
          : Math.round(declC2Total * share);
        const label = getDeclarationDisplayLabel(category);
        this.shadowTable.push(createRow(label, c1, c2, false));
      });

      // Total declaration votes subtotal row - always display
      this.shadowTable.push(createRow('Total declaration votes', declarationTotals[1], declarationTotals[2], true));

      // Total all votes row - use district official 2CP percentages so table matches donut chart
      const totalC1 = (pollingBoothTotals[1] + pollDayAbsent.c1) + (earlyVotingTotals[1] + earlyVotingAbsent.c1) + declarationTotals[1];
      const totalC2 = (pollingBoothTotals[2] + pollDayAbsent.c2) + (earlyVotingTotals[2] + earlyVotingAbsent.c2) + declarationTotals[2];
      const officialC1Percent = foundDistrict.twoCPCandidate1?.twoCPVotePercent ?? null;
      const officialC2Percent = foundDistrict.twoCPCandidate2?.twoCPVotePercent ?? null;
      this.shadowTable.push(createRow('Total all votes', totalC1, totalC2, true, officialC1Percent, officialC2Percent));
    }

    if (this.columns.length > 0) {
      this.hasData = true;
      const output = [];
      for (let i = 0; i < this.shadowTable.length; i++) {
        const newObj: {[k: string]: any} = {};
        for (let j = 0; j < this.columns.length; j++) {
          const tmpName: string = this.columns[j].column;
          const x = tmpName;
          const tmp = this.shadowTable[i][j].isNumber ? +this.shadowTable[i][j].numberData : this.shadowTable[i][j].stringData;
          newObj[tmpName] = tmp;
        }
        output.push(newObj);
      }

      this.displayedColumns = this.columns.map(c => c.column);
      if (this.shadowTable?.length > 0) {
        this.dataSource = new MatTableDataSource(output);
      }
    }
    this.cdr.detectChanges();
  }

  public isBold(data: string): boolean {
    return data === 'Total polling booth ordinary votes' ||
           data === 'Total early voting ordinary votes' ||
           data === 'Total declaration votes' ||
           data === 'Total all votes';
  }

  public isSectionSeparator(data: string): boolean {
    return data === 'Total polling booth ordinary votes' ||
           data === 'Total early voting ordinary votes' ||
           data === 'Total declaration votes';
  }

  public isSectionFirstRow(data: string): boolean {
    return this.earlyVotingFirstRow === data ||
           this.declarationFirstRow === data ||
           this.absentFirstRow === data;
  }

  private earlyVotingFirstRow: string = '';
  private declarationFirstRow: string = '';
  private absentFirstRow: string = '';


  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
